/**
 * Queue Management Page - FULLY MIGRATED TO 3-LAYER ARCHITECTURE
 * Call routing configuration, queue statistics, hold time monitoring
 * Agent availability and queue performance management
 *
 * Migration Status: 100% Complete
 * - Uses QueueRepository for data access
 * - Uses QueueService for business logic
 * - Uses useQueues hooks for React Query integration
 * - Eliminated direct Supabase queries
 * - Client-side computations with useMemo
 * - Optimistic updates for all mutations
 * - Auto-refresh with stale-while-revalidate caching
 */

import React, { useState, useMemo, useEffect } from 'react';
import { AdminPageLayout, StatsGrid } from '@/components/layout/AdminPageLayout';
import { StatCard } from '@/components/layout/AdminStatCard';
import { Card } from '@/components/ui/card';
import { ListChecks, Users, Clock, TrendingUp } from 'lucide-react';
import {
  useQueues,
  useQueueAgents,
  useQueuePhones,
  useAllQueueStats,
  useCreateQueue,
  useUpdateQueue,
  useToggleQueueStatus,
  useAssignAgentToQueue,
  useRemoveAgentFromQueue,
  useAssignPhoneToQueue,
  useRemovePhoneFromQueue,
  useAllPhones,
  useUpdateQueueRoutingPolicy,
  useQueueVoicemailSettings,
  useUpsertQueueVoicemailSettings
} from '@/lib/hooks/useQueues';
import { useActiveAgents } from '@/lib/hooks/useAgents';
import type { CallQueue } from '@/lib/repositories/QueueRepository';

export default function QueueManagement() {
  // React Query hooks - no direct Supabase queries!
  const { data: queues = [], isLoading } = useQueues();
  const { data: agents = [] } = useActiveAgents();
  const { data: allQueueStats = [] } = useAllQueueStats();
  const { data: allPhones = [] } = useAllPhones();
  
  // Mutations
  const createQueue = useCreateQueue();
  const updateQueue = useUpdateQueue();
  const toggleStatus = useToggleQueueStatus();
  const assignAgent = useAssignAgentToQueue();
  const removeAgent = useRemoveAgentFromQueue();
  const assignPhone = useAssignPhoneToQueue();
  const removePhone = useRemovePhoneFromQueue();
  const updateRouting = useUpdateQueueRoutingPolicy();
  const upsertVoicemail = useUpsertQueueVoicemailSettings();

  // UI State (only for modals and forms - no data state!)
  const [selectedQueue, setSelectedQueue] = useState<CallQueue | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showAssignPhoneModal, setShowAssignPhoneModal] = useState(false);
  const [showManageAgentsModal, setShowManageAgentsModal] = useState(false);
  const [newPhoneNumber, setNewPhoneNumber] = useState('');
  const [newQueue, setNewQueue] = useState({
    company_name: '',
    name: '',
    priority: 50,
    max_wait_time: 120,
    is_active: true
  });

  // Load queue-specific data when selected
  const { data: queueAgents = [] } = useQueueAgents(selectedQueue?.id || '', { enabled: !!selectedQueue });
  const { data: queuePhones = [] } = useQueuePhones(selectedQueue?.id || '', { enabled: !!selectedQueue });
  const { data: voicemailSettings = null } = useQueueVoicemailSettings(selectedQueue?.id || '', { enabled: !!selectedQueue });

  // Voicemail local form state
  const [vmEnabled, setVmEnabled] = useState<boolean>(false);
  const [vmGreetingTts, setVmGreetingTts] = useState<string>('');
  const [vmGreetingUrl, setVmGreetingUrl] = useState<string>('');
  const [vmTranscribe, setVmTranscribe] = useState<boolean>(false);

  useEffect(() => {
    if (voicemailSettings) {
      setVmEnabled(!!voicemailSettings.enabled);
      setVmGreetingTts(voicemailSettings.greeting_tts || '');
      setVmGreetingUrl(voicemailSettings.greeting_url || '');
      setVmTranscribe(!!voicemailSettings.transcribe);
    } else {
      setVmEnabled(false);
      setVmGreetingTts('');
      setVmGreetingUrl('');
      setVmTranscribe(false);
    }
  }, [voicemailSettings, selectedQueue?.id]);

  // Client-side computed values using useMemo
  const totalInQueue = useMemo(() => {
    if (!(allQueueStats instanceof Map)) return 0;
    let total = 0;
    allQueueStats.forEach(stats => {
      total += stats.calls_in_queue || 0;
    });
    return total;
  }, [allQueueStats]);

  const longestWait = useMemo(() => {
    if (!(allQueueStats instanceof Map)) return 0;
    let max = 0;
    allQueueStats.forEach(stats => {
      if (stats.longest_wait_seconds > max) {
        max = stats.longest_wait_seconds;
      }
    });
    return max;
  }, [allQueueStats]);

  const availableAgents = useMemo(
    () => agents.filter(a => a.current_status === 'available'),
    [agents]
  );

  // Helper function
  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handlers using mutation hooks
  const handleCreateQueue = async () => {
    const companyName = newQueue.company_name?.trim();
    if (!companyName) return;

    const sysName = (newQueue.name?.trim() || companyName.toLowerCase().replace(/[^a-z0-9]+/g, '-')).slice(0, 64);

    await createQueue.mutateAsync({
      company_name: companyName,
      name: sysName,
      max_wait_time: newQueue.max_wait_time,
      priority: newQueue.priority,
      is_active: newQueue.is_active
    });

    setShowCreateModal(false);
    setNewQueue({
      company_name: '',
      name: '',
      priority: 50,
      max_wait_time: 120,
      is_active: true
    });
  };

  const handleToggleStatus = async (queueId: string, currentStatus: boolean) => {
    await toggleStatus.mutateAsync(queueId);
  };

  const handleUpdateQueue = async () => {
    if (!selectedQueue) return;

    await updateQueue.mutateAsync({
      queueId: selectedQueue.id,
      updates: {
        company_name: selectedQueue.company_name,
        name: selectedQueue.name,
        priority: selectedQueue.priority,
        max_wait_time: selectedQueue.max_wait_time,
        is_active: selectedQueue.is_active
      }
    });

    setShowConfigModal(false);
  };

  const handleSaveRoutingPolicy = async () => {
    if (!selectedQueue) return;

    await updateRouting.mutateAsync({
      queueId: selectedQueue.id,
      policy: {
        routing_default_mode: (selectedQueue as any).routing_default_mode || null,
        default_fallback_ai_agent_id: (selectedQueue as any).default_fallback_ai_agent_id || null,
        default_fallback_timeout_seconds: (selectedQueue as any).default_fallback_timeout_seconds ?? null,
        direct_agent_id: (selectedQueue as any).direct_agent_id || null,
        ring_strategy: (selectedQueue as any).ring_strategy || 'simultaneous'
      }
    });
  };

  const handleSaveVoicemail = async () => {
    if (!selectedQueue) return;
    await upsertVoicemail.mutateAsync({
      queue_id: selectedQueue.id,
      enabled: vmEnabled,
      greeting_tts: vmGreetingTts || null,
      greeting_url: vmGreetingUrl || null,
      transcribe: vmTranscribe
    });
  };

  const handleAssignAgent = async (agentId: string) => {
    if (!selectedQueue) return;

    // Check if already assigned
    if (queueAgents.some(a => a.agent_user_id === agentId)) {
      alert('Agent is already assigned to this queue');
      return;
    }

    await assignAgent.mutateAsync({
      agentId,
      queueId: selectedQueue.id,
      priority: 50
    });
  };

  const handleRemoveAgent = async (assignmentId: string, queueId: string) => {
    await removeAgent.mutateAsync({ assignmentId, queueId });
  };

  const handleAssignPhone = async () => {
    if (!selectedQueue || !newPhoneNumber.trim()) return;

    await assignPhone.mutateAsync({
      phoneNumber: newPhoneNumber.trim(),
      queueId: selectedQueue.id,
      isPrimary: true
    });

    setShowAssignPhoneModal(false);
    setNewPhoneNumber('');
  };

  const handleRemovePhone = async (assignmentId: string, queueId: string) => {
    if (!confirm('Remove this phone number assignment?')) return;
    await removePhone.mutateAsync({ assignmentId, queueId });
  };

  const handleFieldChange = (key: keyof CallQueue, value: any) => {
    if (!selectedQueue) return;
    setSelectedQueue({ ...selectedQueue, [key]: value });
  };

  // Get stats for a specific queue
  const getQueueStats = (queueName: string) => {
    if (allQueueStats instanceof Map) {
      return allQueueStats.get(queueName);
    }
    return undefined;
  };

  // Get unassigned agents for a queue
  const getUnassignedAgents = useMemo(() => {
    if (!selectedQueue) return agents;
    return agents.filter(agent =>
      !queueAgents.some(qa => qa.agent_user_id === agent.user_id)
    );
  }, [agents, queueAgents, selectedQueue]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="text-4xl mb-4">📞</div>
          <div className="text-gray-600">Loading queue data...</div>
        </div>
      </div>
    );
  }

  return (
    <AdminPageLayout
      title="Queue Management"
      subtitle="Configure call routing and monitor queue performance"
      actions={
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
        >
          + Create Queue
        </button>
      }
    >
      {/* Queue Overview Cards */}
      <StatsGrid cols={4}>
        <StatCard
          label="Active Queues"
          value={queues.filter(q => q.is_active).length}
          icon={ListChecks}
          subtitle={`${queues.length} total`}
        />
        <StatCard
          label="Calls in Queue"
          value={totalInQueue}
          icon={Clock}
          variant={totalInQueue > 10 ? 'warning' : 'default'}
          subtitle="across all queues"
        />
        <StatCard
          label="Available Agents"
          value={availableAgents.length}
          icon={Users}
          subtitle={`${agents.length} total agents`}
        />
        <StatCard
          label="Longest Wait"
          value={formatDuration(longestWait)}
          icon={TrendingUp}
          variant={longestWait > 180 ? 'critical' : longestWait > 60 ? 'warning' : 'default'}
          subtitle="current maximum"
        />
      </StatsGrid>

      {/* Queue List */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Call Queues</h2>
        <p className="text-sm text-blue-800 bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          Priority (0–100) controls routing order when multiple queues can receive the same call. Higher numbers ring first.
          Suggested levels: <span className="font-medium">80–100</span> Primary, <span className="font-medium">50–79</span> Secondary, <span className="font-medium">0–49</span> Overflow. Metrics refresh every 5s.
        </p>
        <div className="divide-y divide-gray-200 -mx-6">
          {queues.map((queue) => {
            const stats = getQueueStats(queue.name);

            // Count phone assignments per queue using allPhones (avoids per-queue queries)
            const phoneCount = (allPhones as any[])?.filter(p => p.queue_id === queue.id).length || 0;
            
            return (
              <div
                key={queue.id}
                className="px-6 py-6 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{queue.company_name} - {queue.name}</h3>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        queue.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {queue.is_active ? 'Active' : 'Inactive'}
                      </span>
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        Priority {queue.priority}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">
                      Phone Numbers: {phoneCount > 0 ? `${phoneCount} assigned` : 'None assigned'}
                    </p>
                    
                    {/* Queue Metrics */}
                    <div className="grid grid-cols-5 gap-4">
                      <div>
                        <div className="text-xs text-gray-500">In Queue</div>
                        <div className="text-lg font-bold text-gray-900">{stats?.calls_in_queue || 0}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Max Wait</div>
                        <div className="text-lg font-bold text-gray-900">{formatDuration(stats?.longest_wait_seconds || 0)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Avg Wait</div>
                        <div className="text-lg font-bold text-gray-900">{formatDuration(stats?.average_wait_seconds || 0)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Today's Calls</div>
                        <div className="text-lg font-bold text-gray-900">{stats?.total_calls || 0}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Service Level</div>
                        <div className="text-lg font-bold text-gray-900">
                          {stats ? stats.service_level.toFixed(1) : '0.0'}%
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => handleToggleStatus(queue.id, queue.is_active)}
                      disabled={toggleStatus.isPending}
                      className={`px-3 py-1 rounded font-medium text-sm ${
                        queue.is_active
                          ? 'bg-red-100 text-red-700 hover:bg-red-200'
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      } disabled:opacity-50`}
                    >
                      {queue.is_active ? 'Disable' : 'Enable'}
                    </button>
                    <button
                      className="px-3 py-1 bg-blue-600 text-white rounded font-medium text-sm hover:bg-blue-700"
                      onClick={() => {
                        setSelectedQueue(queue);
                        setShowConfigModal(true);
                      }}
                    >
                      Configure
                    </button>
                  </div>
                </div>
                
                {/* Warning for queue issues */}
                {stats && stats.calls_in_queue > 5 && (
                  <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-center text-yellow-800 text-sm">
                      <span className="mr-2">⚠️</span>
                      High queue volume - Consider adding more agents or increasing routing priority
                    </div>
                  </div>
                )}
                
                {stats && stats.longest_wait_seconds > 300 && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center text-red-800 text-sm">
                      <span className="mr-2">🚨</span>
                      Customer waiting over 5 minutes - Immediate attention required
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          
          {queues.length === 0 && (
            <div className="p-8 text-center text-gray-400">
              No queues configured. Click "Create Queue" to get started.
            </div>
          )}
        </div>
      </Card>

      {/* Queue Configuration Modal */}
      {showConfigModal && selectedQueue && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                Queue Configuration: {selectedQueue.company_name} - {selectedQueue.name}
              </h2>
              <button
                onClick={() => setShowConfigModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* Basic Settings */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Basic Settings</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Company Name
                    </label>
                    <input
                      type="text"
                      value={selectedQueue.company_name}
                      onChange={(e) => handleFieldChange('company_name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Queue Name
                    </label>
                    <input
                      type="text"
                      value={selectedQueue.name}
                      onChange={(e) => handleFieldChange('name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Priority (0-100)
                    </label>
                    <input
                      type="number"
                      value={selectedQueue.priority}
                      onChange={(e) => handleFieldChange('priority', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      min="0"
                      max="100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Max Wait Time (seconds)
                    </label>
                    <input
                      type="number"
                      value={selectedQueue.max_wait_time}
                      onChange={(e) => handleFieldChange('max_wait_time', parseInt(e.target.value || '0', 10))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Routing Policy */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Routing Policy</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Routing Mode</label>
                    <select
                      value={(selectedQueue as any).routing_default_mode || ''}
                      onChange={(e) => handleFieldChange('routing_default_mode' as any, e.target.value || null)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select mode</option>
                      <option value="human_first">Human group first with AI fallback</option>
                      <option value="human_only">Human group only</option>
                      <option value="ai_only">AI agents only</option>
                      <option value="direct_agent">Direct to an agent</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Ring Strategy</label>
                    <select
                      value={(selectedQueue as any).ring_strategy || 'simultaneous'}
                      onChange={(e) => handleFieldChange('ring_strategy' as any, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="simultaneous">Ring all simultaneously</option>
                      <option value="round_robin">Round robin</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Applies to human-first / human-only modes</p>
                  </div>
                </div>

                {(selectedQueue as any).routing_default_mode === 'human_first' && (
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Fallback AI Agent ID</label>
                      <input
                        type="text"
                        value={(selectedQueue as any).default_fallback_ai_agent_id || ''}
                        onChange={(e) => handleFieldChange('default_fallback_ai_agent_id' as any, e.target.value)}
                        placeholder="UUID from AI Agents / Companies page"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Fallback Timeout (seconds)</label>
                      <input
                        type="number"
                        value={(selectedQueue as any).default_fallback_timeout_seconds ?? 0}
                        onChange={(e) =>
                          handleFieldChange(
                            'default_fallback_timeout_seconds' as any,
                            parseInt(e.target.value || '0', 10)
                          )
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        min={0}
                      />
                      <p className="text-xs text-gray-500 mt-1">0 = never fallback automatically</p>
                    </div>
                  </div>
                )}

                {(selectedQueue as any).routing_default_mode === 'direct_agent' && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Direct Agent</label>
                    <select
                      value={(selectedQueue as any).direct_agent_id || ''}
                      onChange={(e) => handleFieldChange('direct_agent_id' as any, e.target.value || null)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select agent assigned to this queue</option>
                      {queueAgents.map((a: any) => (
                        <option key={a.id} value={a.agent_id}>
                          {a.agent_name || a.agent_email || 'Agent'}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">If unavailable, caller will go to voicemail.</p>
                  </div>
                )}

                <div className="flex justify-end mt-4">
                  <button
                    onClick={handleSaveRoutingPolicy}
                    disabled={updateRouting.isPending || !selectedQueue}
                    className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                  >
                    {updateRouting.isPending ? 'Saving...' : 'Save Routing Policy'}
                  </button>
                </div>
              </div>

              {/* Voicemail */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Voicemail</h3>
                <div className="space-y-4">
                  <label className="inline-flex items-center">
                    <input
                      type="checkbox"
                      checked={vmEnabled}
                      onChange={(e) => setVmEnabled(e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Enable Voicemail</span>
                  </label>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Greeting (TTS)</label>
                      <textarea
                        value={vmGreetingTts}
                        onChange={(e) => setVmGreetingTts(e.target.value)}
                        placeholder='e.g., "Please leave a message after the tone."'
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 h-24"
                      />
                      <p className="text-xs text-gray-500 mt-1">Leave empty to use Greeting URL if provided.</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Greeting URL (audio)</label>
                      <input
                        type="url"
                        value={vmGreetingUrl}
                        onChange={(e) => setVmGreetingUrl(e.target.value)}
                        placeholder="https://example.com/greeting.mp3"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">Overrides TTS if provided.</p>
                    </div>
                  </div>

                  <label className="inline-flex items-center">
                    <input
                      type="checkbox"
                      checked={vmTranscribe}
                      onChange={(e) => setVmTranscribe(e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Transcribe voicemail</span>
                  </label>
                </div>
                <div className="flex justify-end mt-4">
                  <button
                    onClick={handleSaveVoicemail}
                    disabled={upsertVoicemail.isPending || !selectedQueue}
                    className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                  >
                    {upsertVoicemail.isPending ? 'Saving...' : 'Save Voicemail Settings'}
                  </button>
                </div>
              </div>

              {/* Agents in Queue */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900">
                    Agents in Queue ({queueAgents.length} assigned, {availableAgents.length} available)
                  </h3>
                  <button
                    onClick={() => setShowManageAgentsModal(true)}
                    className="text-sm px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    Manage Agents
                  </button>
                </div>
                <div className="space-y-2">
                  {queueAgents.map((assignment) => {
                    const agent = agents.find(a => a.user_id === assignment.agent_user_id);
                    const displayName = agent?.name || (assignment as any).agent_name || 'Unknown';
                    const displayStatus = agent?.current_status || (assignment as any).agent_status || 'available';

                    return (
                      <div
                        key={assignment.id}
                        className="flex items-center justify-between p-3 border rounded-lg bg-green-50"
                      >
                        <div className="flex items-center space-x-3">
                          <div className={`w-3 h-3 rounded-full ${
                            displayStatus === 'available' ? 'bg-green-400' :
                            displayStatus === 'busy' ? 'bg-red-400' :
                            'bg-gray-400'
                          }`} />
                          <div>
                            <div className="font-medium text-sm">{displayName}</div>
                            <div className="text-xs text-gray-600 capitalize">{displayStatus}</div>
                          </div>
                        </div>
                        <button
                          onClick={() => handleRemoveAgent(assignment.id, selectedQueue.id)}
                          disabled={removeAgent.isPending}
                          className="text-red-600 hover:text-red-800 text-sm disabled:opacity-50"
                        >
                          Remove
                        </button>
                      </div>
                    );
                  })}

                  {queueAgents.length === 0 && (
                    <div className="text-center text-gray-400 py-4 border-2 border-dashed border-gray-300 rounded-lg">
                      No agents assigned to this queue
                    </div>
                  )}
                </div>
              </div>

              {/* Phone Numbers */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900">
                    Phone Numbers ({queuePhones.length})
                  </h3>
                  <button
                    onClick={() => setShowAssignPhoneModal(true)}
                    className="text-sm px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    + Add Phone
                  </button>
                </div>
                <div className="space-y-2">
                  {queuePhones.map((assignment) => (
                    <div
                      key={assignment.id}
                      className="flex items-center justify-between p-3 border rounded-lg bg-gray-50"
                    >
                      <div>
                        <div className="font-medium text-sm">{assignment.phone_number}</div>
                        {assignment.is_primary && (
                          <span className="text-xs text-blue-600">Primary</span>
                        )}
                      </div>
                      <button
                        onClick={() => handleRemovePhone(assignment.id, selectedQueue.id)}
                        disabled={removePhone.isPending}
                        className="text-red-600 hover:text-red-800 text-sm disabled:opacity-50"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  
                  {queuePhones.length === 0 && (
                    <div className="text-center text-gray-400 py-4 border-2 border-dashed border-gray-300 rounded-lg">
                      No phone numbers assigned
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button 
                  onClick={() => setShowConfigModal(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
                >
                  Cancel
                </button>
                <button 
                  onClick={handleUpdateQueue}
                  disabled={updateQueue.isPending}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50"
                >
                  {updateQueue.isPending ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assign Phone Modal */}
      {showAssignPhoneModal && selectedQueue && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Assign Phone Number
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number (E.164 format)
                </label>
                <input
                  type="tel"
                  value={newPhoneNumber}
                  onChange={(e) => setNewPhoneNumber(e.target.value)}
                  placeholder="+15551234567"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Include country code (e.g., +1 for US/Canada)
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setShowAssignPhoneModal(false);
                  setNewPhoneNumber('');
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleAssignPhone}
                disabled={!newPhoneNumber.trim() || assignPhone.isPending}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {assignPhone.isPending ? 'Assigning...' : 'Assign Phone'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Queue Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Create Queue</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                <input
                  type="text"
                  value={newQueue.company_name}
                  onChange={(e) => setNewQueue({ ...newQueue, company_name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Queue Name</label>
                <input
                  type="text"
                  value={newQueue.name}
                  onChange={(e) => setNewQueue({ ...newQueue, name: e.target.value })}
                  placeholder="auto-generated from company name"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <input
                    type="number"
                    value={newQueue.priority}
                    onChange={(e) => setNewQueue({ ...newQueue, priority: parseInt(e.target.value || '0', 10) })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    min={0}
                    max={100}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Wait Time (seconds)</label>
                  <input
                    type="number"
                    value={newQueue.max_wait_time}
                    onChange={(e) => setNewQueue({ ...newQueue, max_wait_time: parseInt(e.target.value || '0', 10) })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    min={0}
                  />
                </div>
              </div>
              <label className="inline-flex items-center">
                <input
                  type="checkbox"
                  checked={newQueue.is_active}
                  onChange={(e) => setNewQueue({ ...newQueue, is_active: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">Active</span>
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateQueue}
                disabled={createQueue.isPending}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50"
              >
                {createQueue.isPending ? 'Creating...' : 'Create Queue'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manage Agents Modal */}
      {showManageAgentsModal && selectedQueue && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                Manage Agents: {selectedQueue.company_name} - {selectedQueue.name}
              </h2>
              <button
                onClick={() => setShowManageAgentsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              {/* Current Agents in Queue */}
              <div className="mb-6">
                <h3 className="font-semibold text-gray-900 mb-3">Current Agents</h3>
                <div className="space-y-2">
                  {queueAgents.map((assignment) => {
                    const agent = agents.find(a => a.user_id === assignment.agent_user_id);
                    const displayName = agent?.name || (assignment as any).agent_name || 'Unknown';
                    const displayStatus = agent?.current_status || (assignment as any).agent_status || 'available';

                    return (
                      <div
                        key={assignment.id}
                        className="flex items-center justify-between p-3 border rounded-lg bg-gray-50"
                      >
                        <div className="flex items-center space-x-3">
                          <div className={`w-3 h-3 rounded-full ${
                            displayStatus === 'available' ? 'bg-green-400' :
                            displayStatus === 'busy' ? 'bg-red-400' :
                            'bg-gray-400'
                          }`} />
                          <div>
                            <div className="font-medium text-sm">{displayName}</div>
                            <div className="text-xs text-gray-600 capitalize">{displayStatus}</div>
                          </div>
                        </div>
                        <button
                          onClick={() => handleRemoveAgent(assignment.id, selectedQueue.id)}
                          disabled={removeAgent.isPending}
                          className="text-red-600 hover:text-red-800 text-sm disabled:opacity-50"
                        >
                          Remove
                        </button>
                      </div>
                    );
                  })}

                  {queueAgents.length === 0 && (
                    <div className="text-center text-gray-400 py-4 border-2 border-dashed border-gray-300 rounded-lg">
                      No agents assigned to this queue
                    </div>
                  )}
                </div>
              </div>

              {/* Available Agents to Add */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Available Agents</h3>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {getUnassignedAgents.map((agent) => (
                    <div
                      key={agent.id}
                      className="flex items-center justify-between p-3 border rounded-lg bg-white hover:bg-gray-50"
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`w-3 h-3 rounded-full ${
                          agent.current_status === 'available' ? 'bg-green-400' :
                          agent.current_status === 'busy' ? 'bg-red-400' :
                          'bg-gray-400'
                        }`} />
                        <div>
                          <div className="font-medium text-sm">{agent.name}</div>
                          <div className="text-xs text-gray-600 capitalize">{agent.current_status}</div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleAssignAgent(agent.user_id)}
                        disabled={assignAgent.isPending}
                        className="text-blue-600 hover:text-blue-800 text-sm disabled:opacity-50"
                      >
                        Add to Queue
                      </button>
                    </div>
                  ))}

                  {getUnassignedAgents.length === 0 && (
                    <div className="text-center text-gray-400 py-4 border-2 border-dashed border-gray-300 rounded-lg">
                      All agents are already in this queue
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end mt-6 pt-4 border-t">
                <button
                  onClick={() => setShowManageAgentsModal(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminPageLayout>
  );
}
